import { useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { ArrowLeft } from "@/components/animate-ui/icons/arrow-left";
import { ArrowRight } from "@/components/animate-ui/icons/arrow-right";
import { WelcomeScreen } from "@/components/onboarding/screens/welcome";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { BASE_ONBOARDING_SCREENS, CHECKLIST_ONBOARDING_SCREEN } from "@/constants/onboarding";
import { useIsAuthenticated } from "@/lib/auth/hooks";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { ProgressIndicator } from "./progress-indicator";

export function OnboardingDialog() {
	const navigate = useNavigate();
	const isAuthenticated = useIsAuthenticated();
	const {
		isOpen,
		currentScreen,
		checklist,
		closeOnboarding,
		nextScreen,
		prevScreen,
		skipOnboarding,
		goToStep,
	} = useOnboardingStore();

	// Add checklist screen only if all checklist items are completed
	const SCREENS = useMemo(() => {
		const allChecklistItemsCompleted = checklist.every((item) => item.completed);
		return allChecklistItemsCompleted
			? [...BASE_ONBOARDING_SCREENS, CHECKLIST_ONBOARDING_SCREEN]
			: BASE_ONBOARDING_SCREENS;
	}, [checklist]);

	const CurrentScreen = SCREENS[currentScreen]?.component || WelcomeScreen;
	const isFirstScreen = currentScreen === 0;
	const isLastScreen = currentScreen === SCREENS.length - 1;

	const handleNext = () => {
		if (isLastScreen && !isAuthenticated) {
			closeOnboarding();
			navigate({ to: "/signup" });
		} else {
			nextScreen();
		}
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && closeOnboarding()}>
			<DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0">
				{/* Header with progress only */}
				<DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
					<DialogTitle className="sr-only">{SCREENS[currentScreen].title}</DialogTitle>
					<DialogDescription className="sr-only">
						{SCREENS[currentScreen].description}
					</DialogDescription>

					{/* Progress indicator centered */}
					<ProgressIndicator
						currentStep={currentScreen}
						totalSteps={SCREENS.length}
						onStepClick={goToStep}
					/>
				</DialogHeader>

				{/* Scrollable content */}
				<div className="flex-1 overflow-y-auto px-6 py-6">
					<CurrentScreen />
				</div>

				{/* Footer navigation - always visible */}
				<DialogFooter className="px-6 pb-6 pt-4 border-t shrink-0 flex-row items-center justify-between sm:justify-between gap-2">
					{/* Left side: Back button */}
					<Button variant="ghost" onClick={prevScreen} disabled={isFirstScreen} className="gap-2">
						<ArrowLeft className="size-4" />
						Back
					</Button>

					{/* Right side: Skip + Next */}
					<div className="flex items-center gap-2">
						{!isLastScreen && (
							<Button
								variant="ghost"
								size="sm"
								onClick={skipOnboarding}
								className="text-muted-foreground"
							>
								Skip
							</Button>
						)}

						<Button onClick={handleNext} className="gap-2">
							{isLastScreen ? (
								<>
									Get Started
									<ArrowRight className="size-4" />
								</>
							) : (
								<>
									Next
									<ArrowRight className="size-4" />
								</>
							)}
						</Button>
					</div>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
